import React, { memo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { parseDateFromISO8601, stringifyDateToISO8601 } from '../../../util/dates';
import { injectIntl } from '../../../util/reactIntl';
import ControlledDateRangeController from '../../BasicUI/ControlledInputs/ControlledDateRangeController';
import FilterPlain from '../../Filters/FilterPlain/FilterPlain';
import FilterPopup from '../../Filters/FilterPopup/FilterPopup';

// RHF child rendered inside FilterForm's <FormProvider>. Reads the form control
// from context and bridges it to the MUI DateRangeCalendar (via
// ControlledDateRangeController), preserving the `dates` field value shape
// ({ startDate, endDate }) and the imperative controllerRef.onReset API used by
// the clear/cancel handlers below.
const DatesField = ({ controllerRef }) => {
  const { control } = useFormContext();
  return <ControlledDateRangeController control={control} controllerRef={controllerRef} name="dates" />;
};

const getDatesQueryParamName = (queryParamNames) => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'dates';
};

// Parse query parameter, which should look like "2020-05-28,2020-05-31"
const parseValue = (value) => {
  const rawValuesFromParams = value ? value.split(',') : [];
  const [startDate, endDate] = rawValuesFromParams.map((v) => parseDateFromISO8601(v));
  return value && startDate && endDate ? { dates: { startDate, endDate } } : { dates: null };
};
// Format dateRange value for the query. It's given by FieldDateRangeInput:
// { dates: { startDate, endDate } }
const formatValue = (dateRange, queryParamName) => {
  const hasDates = dateRange && dateRange.dates;
  const { startDate, endDate } = hasDates ? dateRange.dates : {};
  const start = startDate ? stringifyDateToISO8601(startDate) : null;
  const end = endDate ? stringifyDateToISO8601(endDate) : null;
  const value = start && end ? `${start},${end}` : null;
  return { [queryParamName]: value };
};

/**
 * Date range filter rendered either as a popup or as a plain inline filter.
 *
 * @param {Object} props
 * @param {string} [props.className] - Custom class applied to the root element.
 * @param {string} [props.rootClassName] - Class overriding the default root class.
 * @param {string} [props.popupClassName] - Class applied to the popup variant.
 * @param {string} [props.buttonClassName] - Class applied to the trigger button.
 * @param {boolean} [props.showAsPopup=true] - Render as popup when true, otherwise plain.
 * @param {boolean} [props.liveEdit=false] - Whether the plain variant edits live.
 * @param {Object} [props.initialValues] - Initial query param values.
 * @param {number} [props.contentPlacementOffset=0] - Popup content offset.
 * @param {string} props.id - Base id for the filter.
 * @param {Function} props.onSubmit - Called with the formatted query value on submit.
 * @param {Array<string>|string} props.queryParamNames - Query param name(s).
 * @param {string} [props.label] - Optional label override.
 * @param {Object} props.intl - react-intl object injected by injectIntl.
 */
export const BookingDateRangeFilterComponent = ({
  className = null,
  rootClassName = null,
  showAsPopup = true,
  initialValues = null,
  id,
  contentPlacementOffset = 0,
  onSubmit,
  queryParamNames,
  label,
  intl,
  popupClassName = null,
  buttonClassName = null,
  liveEdit = false,
  ...rest
}) => {
  const popupControllerRef = useRef(null);
  const plainControllerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const datesQueryParamName = getDatesQueryParamName(queryParamNames);
  const initialDates =
    initialValues && initialValues[datesQueryParamName]
      ? parseValue(initialValues[datesQueryParamName])
      : { dates: null };

  const isSelected = !!initialDates.dates;
  const startDate = isSelected ? initialDates.dates.startDate : null;
  const endDate = isSelected ? initialDates.dates.endDate : null;

  const format = {
    month: 'short',
    day: 'numeric',
  };

  const formattedStartDate = isSelected ? intl.formatDate(startDate, format) : null;
  const formattedEndDate = isSelected ? intl.formatDate(endDate, format) : null;

  const labelForPlain = isSelected
    ? intl.formatMessage(
        { id: 'BookingDateRangeFilter.labelSelectedPlain' },
        {
          dates: `${formattedStartDate} - ${formattedEndDate}`,
        }
      )
    : label
    ? label
    : intl.formatMessage({ id: 'BookingDateRangeFilter.labelPlain' });

  const labelForPopup = isSelected
    ? intl.formatMessage(
        { id: 'BookingDateRangeFilter.labelSelectedPopup' },
        {
          dates: `${formattedStartDate} - ${formattedEndDate}`,
        }
      )
    : label
    ? label
    : intl.formatMessage({ id: 'BookingDateRangeFilter.labelPopup' });

  const handleSubmit = (values) => {
    onSubmit(formatValue(values, datesQueryParamName));
  };

  const onClearPopupMaybe =
    popupControllerRef.current && popupControllerRef.current.onReset
      ? { onClear: () => popupControllerRef.current.onReset(null, null) }
      : {};

  const onCancelPopupMaybe =
    popupControllerRef.current && popupControllerRef.current.onReset
      ? { onCancel: () => popupControllerRef.current.onReset(startDate, endDate) }
      : {};

  const onClearPlainMaybe =
    plainControllerRef.current && plainControllerRef.current.onReset
      ? { onClear: () => plainControllerRef.current.onReset(null, null) }
      : {};

  if (showAsPopup) {
    delete rest.liveEdit;
  }

  return showAsPopup ? (
    <FilterPopup
      className={className}
      contentPlacementOffset={contentPlacementOffset}
      id={`${id}.popup`}
      initialValues={initialDates}
      isOpen={isOpen} // Pass isOpen to FilterPopup
      isSelected={isSelected}
      label={labelForPopup}
      onSubmit={handleSubmit}
      popupClassName={popupClassName}
      rootClassName={rootClassName}
      setIsOpen={setIsOpen} // Pass setIsOpen to FilterPopup
      showAsPopup={showAsPopup}
      {...onClearPopupMaybe}
      {...onCancelPopupMaybe}
      {...rest}
    >
      <DatesField
        controllerRef={(node) => {
          popupControllerRef.current = node;
        }}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      contentPlacementOffset={contentPlacementOffset}
      id={`${id}.plain`}
      isSelected={isSelected}
      label={labelForPlain}
      liveEdit
      onSubmit={handleSubmit}
      rootClassName={rootClassName}
      {...onClearPlainMaybe}
      initialValues={initialDates}
      isOpen={isOpen} // Pass isOpen to FilterPlain if needed
      setIsOpen={setIsOpen} // Pass setIsOpen to FilterPlain
      {...rest}
    >
      <DatesField
        controllerRef={(node) => {
          plainControllerRef.current = node;
        }}
      />
    </FilterPlain>
  );
};

const BookingDateRangeFilter = memo(injectIntl(BookingDateRangeFilterComponent));

export default BookingDateRangeFilter;
